import { useRef, useState } from 'react';
import { runInAction } from 'mobx';
import { UserEntity } from 'Models/Entities';
import { registerForCustomer } from 'Services/Api/_HumanWritten/RegisterService';
import alertToast from 'Util/ToastifyUtils';
import { isNotNullOrUndefined } from 'Util/TypeGuards';

/**
 * Shared state and submit logic for a staff member registering a new customer.
 * Used by both the register-customer page and the inline register step in the purchase modals.
 */
export function useRegisterCustomerForm() {
	const [userEntity] = useState(() => new UserEntity());
	const [loading, setLoading] = useState(false);
	const [errors, setErrors] = useState<Record<string, string>>({});

	// Whether the staff member ticked "Register as a company" for this customer. Only offered while
	// the User.companyName.enabled config is on.
	const [registerAsCompany, setRegisterAsCompanyState] = useState(false);

	// Validation errors should only appear after the first submit attempt.
	const firstSubmit = useRef(false);

	// After the first submit, re-validate on every change so field errors clear/update live. The
	// company flag is passed explicitly because the toggle has to validate against the state it is
	// moving to, not the one React still holds.
	const runValidation = (isCompany: boolean) => {
		if (firstSubmit.current) {
			setErrors(userEntity.validateRegistration({ isCompany }));
		}
	};

	// Takes no arguments on purpose: it is handed straight to field onAfterChange callbacks, some of
	// which invoke it with the changed value (PhoneInputField passes the phone number).
	const revalidate = () => runValidation(registerAsCompany);

	/**
	 * Toggles the company option. Unticking drops the company name so an abandoned value is never
	 * submitted, and re-validates against the new state rather than the stale one.
	 */
	const setRegisterAsCompany = (checked: boolean) => {
		setRegisterAsCompanyState(checked);
		if (!checked) {
			runInAction(() => {
				userEntity.companyName = undefined;
			});
		}
		runValidation(checked);
	};

	/**
	 * Validates and registers the customer. Resolves with the created user (id populated from the
	 * response) on success, or null when validation fails or the request errors (an error toast is
	 * shown for the latter).
	 */
	const submit = async (): Promise<UserEntity | null> => {
		firstSubmit.current = true;

		const validationErrors = userEntity.validateRegistration({ isCompany: registerAsCompany });
		setErrors(validationErrors);
		if (Object.keys(validationErrors).length > 0) {
			return null;
		}

		try {
			setLoading(true);
			const response = await registerForCustomer(userEntity);
			userEntity.id = response.data;
			return userEntity;
		} catch (error) {
			// @ts-expect-error
			if (isNotNullOrUndefined(error?.response?.data)) {
				// @ts-expect-error
				alertToast(error?.response?.data, 'error');
			} else {
				alertToast('Failed to create new user', 'error');
			}
			return null;
		} finally {
			setLoading(false);
		}
	};

	return {
		userEntity,
		errors,
		loading,
		registerAsCompany,
		setRegisterAsCompany,
		revalidate,
		submit,
	};
}
